import { addQueryParams, createUrl, restGet, restPost } from '~/src/dash/api/rest';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { BidErrorKey, CampaignNoFillErrorKey } from '~/src/dash/utils/bidErrorCodes';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { ECurrencyType } from '~/src/dash/api/exchange-rates';

type AuctionErrorKey = BidErrorKey | CampaignNoFillErrorKey;

export interface AuctionInsightsNode {
  name: string;
  total: number;
  percentage: number;
  values: Record<AuctionErrorKey, number>;
}

export type AuctionInsightKey =
  | 'TotalAdserverInventoryRequest'
  | 'TotalAdserverRequest'
  | 'TotalBidRequested'
  | 'TotalAdserverFilled'
  | 'TotalPacingRejected'
  | 'TotalBidResponse'
  | 'TotalInvalidCreatives'
  | 'TotalAuctionBids'
  | 'TotalBidResponseErrors'
  | 'TotalNoBid'
  | 'TotalBidTimeout'
  | 'TotalValidBids'
  | 'TotalInvalidBids'
  | 'TotalUnwrapErrors'
  | 'TotalBidWon'
  | 'TotalBidLost'
  | 'TotalValidBidsRejected'
  | 'TotalBidderImpression'
  | 'TotalBidderNoImpression'
  | 'TotalConnectionErrors';

interface AuctionInsightsLink {
  source: AuctionInsightKey;
  target: AuctionInsightKey;
  value: number;
}

export interface AuctionInsightsData {
  nodes: Record<AuctionInsightKey, AuctionInsightsNode>;
  links: AuctionInsightsLink[];
}

export interface IGetCampaignAuctionInsightsQueryParams {
  PublisherID: number;
  CampaignID: number;
  StartDate: string;
  EndDate: string;
  Timezone: string;
  LiveDensityRange: string;
  IsRealTimeOnly: boolean;
}

export async function getCampaignAuctionInsights(
  publisherID: string,
  query: unknown,
): Promise<unknown> {
  const reqUrl = `${createUrl('/campaign_auction_insights', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query);
  return (response?.parsedBody as { Insights: unknown })?.Insights || {};
}

export interface IGetBidderAuctionInsightsQueryParams {
  PublisherID: number;
  BidderID: number;
  StartDate: string;
  EndDate: string;
  Timezone: string;
  LiveDensityRange: string;
  IsRealTimeOnly: boolean;
}

export async function getBidderAuctionInsights(
  publisherID: string,
  query: unknown,
  signal?: AbortSignal,
): Promise<unknown> {
  const reqUrl = `${createUrl('/bidder_auction_insights', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query, undefined, undefined, signal);
  return (response?.parsedBody as { Insights: unknown })?.Insights || {};
}

// Base interface with common Sankey params
export interface IDashboardAuctionInsightsSankeyDataQueryParamsBase {
  PublisherID: number;
  Timezone: string;
  StartDate: string;
  EndDate: string;
  LiveDensityRange: string;
  IsRealTimeOnly: boolean;
}

// Bidders-specific extends base
export interface IGetDashboardBidderAuctionInsightsSankeyDataQueryParams
  extends IDashboardAuctionInsightsSankeyDataQueryParamsBase {
  BidderIDs: number[];
  SiteIDs: number[];
  BidderCodes: string[];
}

export async function getDashboardBidderAuctionInsightsSankeyData(
  publisherID: string,
  query: IGetDashboardBidderAuctionInsightsSankeyDataQueryParams,
  signal?: AbortSignal,
): Promise<AuctionInsightsData> {
  const reqUrl = `${createUrl('/auction_insights/dashboard_bidders_sankey', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query, undefined, undefined, signal);
  return (
    (response?.parsedBody as { Insights: AuctionInsightsData })?.Insights || {
      nodes: [],
      links: [],
    }
  );
}

export interface IFetchDemandSourcesData {
  DemandSources: ISelectOption[];
  CustomDemandSources: ISelectOption[];
  CustomDemandSourceToBidderIDs: Record<string, number[]>;
}

export async function fetchDemandSourcesData(
  publisherID: string,
  siteIDs?: number[],
): Promise<ISelectOption[]> {
  const baseUrl = createUrl('/auction_insights/dashboard_demand_sources', publisherID, 'v1');
  const reqUrl = addQueryParams(baseUrl, { site_ids: siteIDs?.join(',') || '' });
  try {
    const response = await restGet<IFetchDemandSourcesData>(reqUrl, {});
    return response.parsedBody?.DemandSources as ISelectOption[];
  } catch (err) {
    throw err;
  }
}

export const generateDashboardDemandSourcesOptions = async (
  session: ISessionState,
  user: IUserState,
  args?: { siteIDs?: number[] },
): Promise<ISelectOption[]> => {
  try {
    const demandSources = await fetchDemandSourcesData(session.publisher.ID, args?.siteIDs || []);
    // Get the demand source adapters to map keys to display text
    const demandSourceAdapters = getHeaderBidderAdaptersForPublisher(
      '',
      true,
      session.publisher,
      hasRoleSuperUser(user.user),
      true,
    );
    // Create a map for quick lookup of adapter text by key
    const adapterMap = new Map(demandSourceAdapters.map((adapter) => [adapter.key, adapter.text]));
    // Replace labels for DemandSources using the adapter text (only for non-custom demand sources)
    const updatedDemandSources = demandSources.map((demandSource) => {
      // Skip label replacement for custom demand sources
      if (demandSource.isCustom) {
        return demandSource;
      }
      const adapterText = adapterMap.get(String(demandSource.id));
      return {
        ...demandSource,
        label: adapterText || demandSource.label,
      };
    });

    return updatedDemandSources;
  } catch (err) {
    console.error(err);
    return [] as ISelectOption[];
  }
};

// Base interface with common table params
export interface IDashboardAuctionInsightsTableQueryParamsBase {
  PublisherID: number;
  CurrencyDisplayed: ECurrencyType;
  Timezone: string;
  StartDate: string;
  EndDate: string;
  LiveDensityRange: string;
  IsRealTimeOnly: boolean;
  OrderBy: string;
  OrderDirection: string;
  Limit: number;
  Offset: number;
}

// Bidders-specific extends base
export interface IDashboardBidderAuctionInsightsTableQueryParams
  extends IDashboardAuctionInsightsTableQueryParamsBase {
  BidderCodes: string[];
  BidderIDs: number[];
  SiteIDs: number[];
}

export interface IDashboardAuctionInsightsTableRow {
  ID: number;
  Name: string;
  DemandSource: string;
  BidRequests: number;
  BidResponses: number;
  Impressions: number;
  Revenue: number;
}

export interface IGetDashboardBidderAuctionInsightsTableData {
  Data: IDashboardAuctionInsightsTableRow[];
  TotalCount: number;
  CurrencyDisplayed: ECurrencyType;
}

export async function getDashboardBidderAuctionInsightsTableData(
  publisherID: string,
  query: IDashboardBidderAuctionInsightsTableQueryParams,
  signal?: AbortSignal,
): Promise<IGetDashboardBidderAuctionInsightsTableData> {
  const reqUrl = `${createUrl('/auction_insights/dashboard_bidders_table', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query, undefined, undefined, signal);
  return (
    (response?.parsedBody as IGetDashboardBidderAuctionInsightsTableData) || {
      Data: [],
      TotalCount: 0,
    }
  );
}

export interface IGetDealAuctionInsightsQueryParams {
  PublisherID: number;
  DealID: number;
  StartDate: string;
  EndDate: string;
  Timezone: string;
  LiveDensityRange: string;
  IsRealTimeOnly: boolean;
}

export async function getDealAuctionInsights(
  publisherID: string,
  query: unknown,
): Promise<unknown> {
  const reqUrl = `${createUrl('/deal_auction_insights', publisherID, 'v1')}`;
  const response = await restPost<unknown>(reqUrl, query);
  return (response?.parsedBody as { Insights: unknown })?.Insights || {};
}
